import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

// AZ ÖSSZES IMPORTNAK LEGFELÜL KELL LENNIE
// @ts-ignore
import alloVid from '../videos/allo.MOV';
// @ts-ignore
import fekvoVid from '../videos/fekvo.MOV';
// @ts-ignore
import firstBg from '../videos/first_background.mov';
// @ts-ignore
import firstVid from '../videos/first_video.mov';
// @ts-ignore
import musicFile from '../videos/music.mp3'; 

const MuteIcon = FaVolumeMute as any;
const UpIcon = FaVolumeUp as any;

const Hero: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentLoop, setCurrentLoop] = useState<'bg' | 'vid'>('bg');
  const [isMuted, setIsMuted] = useState(false);
  
  const [showRsvpLink, setShowRsvpLink] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); 

  const currentEnvelopeVid = isMobile ? `${alloVid}#t=0.001` : `${fekvoVid}#t=0.001`;

  const envelopeRef = useRef<HTMLVideoElement>(null);
  const bgRef = useRef<HTMLVideoElement>(null);
  const vidRef = useRef<HTMLVideoElement>(null);

  // ÚJ HANGKEZELŐ REF: Sokkal egyszerűbb és bypassolja a némító gombot
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // EGYSZERŰSÍTETT HTML5 AUDIO KEZELÉS
    const bgAudio = new Audio(musicFile);
    bgAudio.loop = true;
    audioRef.current = bgAudio;

    return () => { 
      document.body.style.overflow = 'unset'; 
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setShowRsvpLink(true);
      }, 2500); 
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleOpen = () => {
    if (!hasStarted) {
      setHasStarted(true);
      
      if (envelopeRef.current) {
        envelopeRef.current.play().catch(e => console.log("Video play error:", e));
      }
      
      // HANG INDÍTÁSA
      if (audioRef.current) {
        audioRef.current.play().catch(e => console.log("Audio play error:", e));
        setIsMuted(false);
      }
    }
  };

  const handleEnvelopeTimeUpdate = () => {
    if (envelopeRef.current) {
      const remainingTime = envelopeRef.current.duration - envelopeRef.current.currentTime;
      if (remainingTime < 1.5 && !isOpen) {
        setIsOpen(true);
        bgRef.current?.play().catch(e => console.log(e));
        document.body.style.overflow = 'unset';
      }
    }
  };

  const handleBgEnded = () => {
    setCurrentLoop('vid');
    setTimeout(() => { vidRef.current?.play().catch(e => console.log(e)); }, 10);
  };

  const handleVidEnded = () => {
    setCurrentLoop('bg');
    setTimeout(() => { bgRef.current?.play().catch(e => console.log(e)); }, 10);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.muted = false;
        setIsMuted(false);
      } else {
        audioRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  const scrollToRSVP = () => {
    const element = document.getElementById('rsvp-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        height: '100vh', 
        width: { xs: '100vw', md: 'auto' }, 
        overflow: 'hidden', 
        bgcolor: '#000' 
      }}
    >
      
      {/* MUTE GOMB */}
      {hasStarted && (
        <IconButton 
          onClick={(e) => {
            e.stopPropagation(); 
            toggleMute();
          }}
          sx={{ 
            position: 'fixed', bottom: 30, right: 30, zIndex: 10000, 
            bgcolor: 'rgba(0, 0, 0, 0.2)', color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          {isMuted ? <MuteIcon size={24} /> : <UpIcon size={24} />}
        </IconButton>
      )}

      {/* BORÍTÉK LAYER */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 2 } }}
            onClick={handleOpen}
            style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              zIndex: 9999, cursor: 'pointer', backgroundColor: '#000',
              display: 'flex', justifyContent: 'center', alignItems: 'center' 
            }}
          >
            <Box
              component="video" 
              ref={envelopeRef}
              onTimeUpdate={handleEnvelopeTimeUpdate}
              muted 
              playsInline
              preload="auto" 
              src={currentEnvelopeVid}
              sx={{
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
              }}
            />

            {/* === ÚJ: PULZÁLÓ ANIMÁCIÓ TISZTA CSS-SEL (Vajsima, nem akad) === */}
            {!hasStarted && (
              <Box sx={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none', // Átengedi a kattintást a videóra
                
                // CSS Animáció (Keyframes) definiálása:
                '@keyframes ripple': {
                  '0%': {
                    transform: 'scale(0.3)', // Egészen kicsiről indul a közepéből
                    opacity: 1,              // Teljesen láthatóan kezd
                  },
                  '100%': {
                    transform: 'scale(3.5)', // Jó nagyra megnő
                    opacity: 0,              // És a végére teljesen elhalványul
                  },
                }
              }}>
                {/* 3 egymást követő kör, tökéletes szinkronban */}
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      border: '2px solid #c5b382', // Matt arany színünk
                      backgroundColor: 'rgba(197, 179, 130, 0.15)',
                      opacity: 0, // Alapból láthatatlan, az animáció vezérli
                      
                      // 3 másodperces animáció, végtelenítve, szép lágy görbével (cubic-bezier)
                      animation: 'ripple 3s infinite cubic-bezier(0.1, 0.5, 0.3, 1)',
                      
                      // Itt a varázslat: 3 kör van, 3 másodperc az animáció. 
                      // 0s, 1s, és 2s késleltetéssel indulnak, így TÖKÉLETESEN folyamatos a lüktetés.
                      animationDelay: `${i * 1}s`, 
                    }}
                  />
                ))}
              </Box>
            )}
            {/* ========================================================= */}

          </motion.div>
        )}
      </AnimatePresence>

      {/* HÁTTÉR VIDEÓK LAYER */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
        <video
          ref={bgRef} onEnded={handleBgEnded}
          style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', display: currentLoop === 'bg' ? 'block' : 'none' }}
          muted playsInline
        >
          <source src={firstBg} type="video/quicktime" />
          <source src={firstBg} type="video/mp4" />
        </video>
        
        <video
          ref={vidRef} onEnded={handleVidEnded}
          style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', display: currentLoop === 'vid' ? 'block' : 'none' }}
          muted playsInline
        >
          <source src={firstVid} type="video/quicktime" />
          <source src={firstVid} type="video/mp4" />
        </video>
        <Box sx={{ position: 'absolute', width: '100%', height: '100%', background: 'rgba(0,0,0,0.3)' }} />
      </Box>

      {/* SZÖVEG LAYER */}
      <Box sx={{ 
        height: '100vh', display: 'flex', flexDirection: 'column', 
        justifyContent: 'center', alignItems: 'center', textAlign: 'center',
        position: 'relative', zIndex: 2, color: 'white'
      }}>
        {/* KÖZÉPSŐ FŐCÍM */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 2, ease: "easeOut" }}
          >
            <Typography variant="h1" sx={{ fontFamily: '"AMORESA"', fontSize: { xs: '3.5rem', md: '6rem' }, fontWeight: 400 }}>
              Meli & Máté
            </Typography>

            {/* === VONAL-KÖRVONALAS SZÍV-VONAL ELRENDEZÉS === */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 3 }}>
              <Box sx={{ width: { xs: '50px', md: '70px' }, height: '1px', bgcolor: '#c5b382' }} />
              <FavoriteBorderIcon sx={{ color: '#c5b382', fontSize: '1.4rem', opacity: 0.9 }} />
              <Box sx={{ width: { xs: '50px', md: '70px' }, height: '1px', bgcolor: '#c5b382' }} />
            </Box>
            {/* ============================================== */}

            <Typography variant="h6" sx={{ fontFamily: '"Blosta"', letterSpacing: 8, textTransform: 'uppercase', fontSize: '1.1rem', fontWeight: 300 }}>
              2026. Augusztus 21.
            </Typography>
          </motion.div>
        )}

        {/* ÚJ POZÍCIÓ: RSVP LINK A KÉPERNYŐ ALJÁN */}
        <AnimatePresence>
          {showRsvpLink && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              style={{
                position: 'absolute',
                bottom: '90px',
                left: 0,
                width: '100%',
                textAlign: 'center',
                zIndex: 3 
              }}
            >
              <Typography 
                onClick={scrollToRSVP}
                variant="h6" 
                sx={{ 
                  fontFamily: '"Blosta"', 
                  fontSize: '1.3rem', 
                  color: '#ffffff', 
                  cursor: 'pointer',
                  textUnderlineOffset: '4px',
                  '&:hover': {
                    color: '#fff',
                    transition: 'color 0.3s'
                  }
                }}
              >
                Velünk ünnepelsz?
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default Hero;